import type { WorkoutSegment } from '@/types';

// Default fallback speed: 3.33 m/s = 12 km/h = 5:00 min/km
const DEFAULT_VISUALIZATION_SPEED = 3.33;

export const parseZwoToSegments = (xmlString: string): WorkoutSegment[] => {
  if (!xmlString) return [];
  
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");
    
    // Try to find <workout> tag, possibly inside <workout_file> or just root
    let workout = xmlDoc.getElementsByTagName("workout")[0];
    
    if (!workout) return [];

    const segments: WorkoutSegment[] = [];
    let currentTime = 0;

    Array.from(workout.children).forEach((node: Element) => {
      const tagName = node.tagName;
      
      // Get text event if exists (simple check)
      const textEvent = node.getElementsByTagName('textevent')[0];
      const textMessage = textEvent ? textEvent.getAttribute('message') || undefined : undefined;

      // Helper to calculate duration from distance safely
      const calculateDuration = (dist: number, targetVal: number): number => {
          if (dist <= 0) return 0;
          
          let speed = targetVal;
          
          // CRITICAL FIX FOR RUNNING:
          // If the AI put a "coefficient" (e.g. 0.8) into the Power/Speed field instead of raw Speed (m/s),
          // or if it put 0.0 or a very small number, division causes issues.
          
          // If speed is practically zero or negative, assume default speed
          if (!speed || speed <= 0.001) {
              speed = DEFAULT_VISUALIZATION_SPEED;
          }
          // If speed is a low coefficient (0.001 < speed < 0.5), scale it by default speed
          else if (speed < 0.5) {
              speed = speed * DEFAULT_VISUALIZATION_SPEED;
          }
          
          return Math.round(dist / speed);
      };

      if (tagName === 'IntervalsT') {
        const repeat = parseInt(node.getAttribute('Repeat') || '1');
        let onDur = parseInt(node.getAttribute('OnDuration') || '0');
        let offDur = parseInt(node.getAttribute('OffDuration') || '0');
        
        // Try parsing Power first, then Speed
        let onVal = parseFloat(node.getAttribute('OnPower') || '0');
        let offVal = parseFloat(node.getAttribute('OffPower') || '0');
        
        // If Power is 0, check for Speed (Running Pace mode)
        const isOnSpeed = node.hasAttribute('OnSpeed');
        if (onVal === 0 && isOnSpeed) {
           onVal = parseFloat(node.getAttribute('OnSpeed') || '0');
           offVal = parseFloat(node.getAttribute('OffSpeed') || '0');
        }

        // VISUALIZATION FIX: Handle Distance-based Intervals (OnDist/OffDist)
        const onDist = parseFloat(node.getAttribute('OnDist') || '0');
        const offDist = parseFloat(node.getAttribute('OffDist') || '0');

        if (onDur === 0 && onDist > 0) {
            onDur = calculateDuration(onDist, onVal);
        }

        if (offDur === 0 && offDist > 0) {
            offDur = calculateDuration(offDist, offVal);
        }

        // Safety clamp for durations (Min 1s, Max 24 hours)
        if (!isFinite(onDur) || onDur < 1) onDur = 300; 
        if (onDur > 86400) onDur = 300;

        if (!isFinite(offDur) || offDur < 1) offDur = 300;
        if (offDur > 86400) offDur = 300;

        for (let i = 0; i < repeat; i++) {
          // ON
          segments.push({
            time: currentTime,
            duration: onDur,
            powerLow: onVal,
            powerHigh: onVal,
            type: 'IntervalsT',
            text: textMessage ? `${textMessage} (On)` : `Interval ${i + 1}/${repeat}`
          });
          currentTime += onDur;

          // OFF
          segments.push({
            time: currentTime,
            duration: offDur,
            powerLow: offVal,
            powerHigh: offVal,
            type: 'IntervalsT',
            text: 'Recovery'
          });
          currentTime += offDur;
        }
      } else {
        let duration = parseInt(node.getAttribute('Duration') || '0');
        let powerLow = parseFloat(node.getAttribute('PowerLow') || '0');
        let powerHigh = parseFloat(node.getAttribute('PowerHigh') || '0');
        
        // Check for SteadyState Power or Speed
        if (tagName === 'SteadyState') {
          const power = parseFloat(node.getAttribute('Power') || '0');
          const speed = parseFloat(node.getAttribute('Speed') || '0');
          
          if (power > 0) {
            powerLow = power;
            powerHigh = power;
          } else if (speed > 0) {
            powerLow = speed; // Reuse power field for chart visualization
            powerHigh = speed;
          }
        }
        
        // Handle Warmup/Cooldown Speed if applicable (rare in basic ZWO, but possible)
        if ((tagName === 'Warmup' || tagName === 'Cooldown') && powerLow === 0 && powerHigh === 0) {
             const speedLow = parseFloat(node.getAttribute('SpeedLow') || '0');
             const speedHigh = parseFloat(node.getAttribute('SpeedHigh') || '0');
             if (speedLow > 0 || speedHigh > 0) {
                 powerLow = speedLow;
                 powerHigh = speedHigh;
             }
        }

        // VISUALIZATION FIX: Handle Distance-based SteadyState
        const dist = parseFloat(node.getAttribute('Dist') || '0');
        if (duration === 0 && dist > 0) {
             const avgSpeed = (powerHigh + powerLow) / 2;
             duration = calculateDuration(dist, avgSpeed);
        }

        // Safety clamp
        if (!isFinite(duration) || duration < 1) duration = 300;
        if (duration > 86400) duration = 300;

        segments.push({
          time: currentTime,
          duration: duration,
          powerLow: powerLow,
          powerHigh: powerHigh,
          type: tagName as any,
          text: textMessage
        });
        currentTime += duration;
      }
    });

    return segments;
  } catch (e) {
    console.error("Error parsing XML:", e);
    return [];
  }
};

export const downloadZwo = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};